/**
 * writeQueue.ts
 *
 * IndexedDB-backed queue for Firestore write operations that fail while offline.
 * Each op is serialisable (set / update / delete with a document path and plain
 * data object), so it can be stored in IDB and replayed later.
 *
 * Used by DataContext to:
 *  1. Enqueue failed Firestore writes when `navigator.onLine === false`
 *     instead of rolling back optimistic UI.
 *  2. Drain the queue when connectivity is restored — via the `online` event
 *     (all browsers) or a `FLUSH_WRITE_QUEUE` SW message (Chrome/Edge
 *     Background Sync, fires even after the app is closed).
 */

export type WriteOp =
  | { type: 'set';    path: string; data: Record<string, unknown>; merge?: boolean }
  | { type: 'update'; path: string; data: Record<string, unknown> }
  | { type: 'delete'; path: string };

export interface PendingWrite {
  /** Deterministic ID — typically the Firestore doc path or an optimistic entry ID.
   *  `put()` is used so enqueueing the same ID twice is idempotent. */
  id: string;
  op: WriteOp;
  enqueuedAt: number;
}

const DB_NAME    = 'kodacare_writes';
const STORE      = 'pending_writes';
const DB_VERSION = 1;

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = (e) => resolve((e.target as IDBOpenDBRequest).result);
    req.onerror  = (e) => reject((e.target as IDBOpenDBRequest).error);
  });
}

export async function enqueueWrite(item: PendingWrite): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(item); // put = upsert, idempotent by id
    tx.oncomplete = () => resolve();
    tx.onerror    = (e) => reject((e.target as IDBRequest).error);
  });
}

export async function getPendingWrites(): Promise<PendingWrite[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result as PendingWrite[]);
    req.onerror   = (e) => reject((e.target as IDBRequest).error);
  });
}

export async function dequeueWrite(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror    = (e) => reject((e.target as IDBRequest).error);
  });
}
